const TRACK_SELECTOR = '.trusted-by-logos > .group-block-content, .luotettu-ka-logoja > .group-block-content';
const MIN_ITEMS_FOR_LOOP = 2;
const PX_PER_SECOND = 80;
const IMAGE_READY_TIMEOUT = 3000;
const RESIZE_DEBOUNCE = 150;

// Aspect ratio (width / height) of a typical wide wordmark. Logos at or above
// this aspect render at the base height (scale 1); narrower/squarer logos are
// scaled up toward MAX_SCALE so every logo carries roughly equal visual weight.
const REFERENCE_ASPECT = 4;
const MAX_SCALE = 1.6;

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function applyLogoScale(imageBlock) {
  const img = imageBlock.querySelector('.image-block__image');
  if (!img || !img.naturalWidth || !img.naturalHeight) return;

  const aspect = img.naturalWidth / img.naturalHeight;
  let scale = Math.sqrt(REFERENCE_ASPECT / aspect);
  scale = Math.min(Math.max(scale, 1), MAX_SCALE);
  imageBlock.style.setProperty('--logo-scale', scale.toFixed(3));
}

function whenImageReady(img) {
  if (img.complete) return Promise.resolve();
  return new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
  });
}

// Resolve when every image is ready, but never wait longer than the timeout so
// a slow/lazy image can't stall the marquee.
function whenImagesReady(images) {
  return Promise.race([
    Promise.all(images.map(whenImageReady)),
    new Promise((resolve) => {
      setTimeout(resolve, IMAGE_READY_TIMEOUT);
    }),
  ]);
}

function getOriginalItems(track) {
  return Array.from(track.children).filter(
    (child) => child.classList.contains('image-block') && child.dataset.trustedClone !== 'true'
  );
}

// (Re)build a single marquee track. Idempotent: safe to call on load, on
// shopify:section:load, on resize, and on reduced-motion changes.
function build(track) {
  // Remove any clones from a previous build so we measure a single clean set.
  track.querySelectorAll(':scope > [data-trusted-clone="true"]').forEach((clone) => clone.remove());

  const items = getOriginalItems(track);
  if (items.length < MIN_ITEMS_FOR_LOOP) return;

  // Concurrency guard: if a newer build starts, this one aborts before it
  // mutates the DOM (prevents overlapping async builds from fighting).
  const generation = (Number(track.dataset.trustedLoopGeneration) || 0) + 1;
  track.dataset.trustedLoopGeneration = String(generation);

  const images = items
    .map((item) => item.querySelector('.image-block__image'))
    .filter(Boolean);

  // Load eagerly so off-screen clones don't pop or rescale when scrolled into view.
  images.forEach((img) => {
    img.loading = 'eager';
  });

  whenImagesReady(images).then(() => {
    if (Number(track.dataset.trustedLoopGeneration) !== generation) return;

    // Size every logo to its final dimensions BEFORE measuring or cloning.
    items.forEach(applyLogoScale);

    // Reduced motion: single static set, no clones, no animation distance.
    if (reducedMotionQuery.matches) {
      track.style.removeProperty('--trusted-loop-shift');
      track.style.removeProperty('--trusted-loop-duration');
      return;
    }

    requestAnimationFrame(() => {
      if (Number(track.dataset.trustedLoopGeneration) !== generation) return;

      // Width of one full set of logos (only the originals are in the track now).
      const setWidth = track.scrollWidth;
      if (!setWidth) return;

      const viewport = (track.parentElement && track.parentElement.clientWidth) || setWidth;

      // Duplicate the set until the track spans at least one viewport beyond a
      // single set, so translating by one period never exposes a gap.
      const copies = Math.max(2, Math.ceil(viewport / setWidth) + 1);
      for (let i = 1; i < copies; i += 1) {
        const cloneFragment = document.createDocumentFragment();
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.trustedClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          cloneFragment.appendChild(clone);
        });
        track.appendChild(cloneFragment);
      }

      // Translate by exactly one set = (100 / copies)% of the whole track.
      // The track is `copies` identical sets, so a percentage of it is exact and
      // immune to pixel-measurement quirks (gap/margin/scrollWidth). Duration is
      // still derived from the pixel width so scroll speed stays constant.
      const shift = 100 / copies;
      const duration = Math.max(setWidth / PX_PER_SECOND, 12);
      track.style.setProperty('--trusted-loop-shift', `${shift.toFixed(4)}%`);
      track.style.setProperty('--trusted-loop-duration', `${duration}s`);
    });
  });
}

function bindHoverPause(track) {
  if (track.dataset.trustedLoopBound === 'true') return;

  track.addEventListener('mouseenter', () => {
    track.style.animationPlayState = 'paused';
  });
  track.addEventListener('mouseleave', () => {
    track.style.animationPlayState = 'running';
  });
  track.addEventListener('focusin', () => {
    track.style.animationPlayState = 'paused';
  });
  track.addEventListener('focusout', () => {
    track.style.animationPlayState = 'running';
  });
  track.dataset.trustedLoopBound = 'true';
}

function setupTrustedByLoop(track) {
  if (!(track instanceof HTMLElement)) return;
  bindHoverPause(track);
  build(track);
}

function initTrustedByLoops(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  root.querySelectorAll(TRACK_SELECTOR).forEach(setupTrustedByLoop);
}

function rebuildAllTracks() {
  document.querySelectorAll(TRACK_SELECTOR).forEach((track) => {
    if (track instanceof HTMLElement) build(track);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTrustedByLoops());
} else {
  initTrustedByLoops();
}

document.addEventListener('shopify:section:load', (event) => {
  initTrustedByLoops(event.target);
});

let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(rebuildAllTracks, RESIZE_DEBOUNCE);
});

// Re-render when the visitor toggles their reduced-motion preference.
if (typeof reducedMotionQuery.addEventListener === 'function') {
  reducedMotionQuery.addEventListener('change', rebuildAllTracks);
} else if (typeof reducedMotionQuery.addListener === 'function') {
  reducedMotionQuery.addListener(rebuildAllTracks);
}
